import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';
import GradientButton from '../components/GradientButton';
import { colors } from '../theme';

interface Job {
  id: string;
  status: string;
  category: string;
  issueDescription: string;
  techName?: string;
  createdAt?: Timestamp;
  pricing?: { hourlyRate?: number };
  jobType?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  wifi_networking: '📶 Wi-Fi & Networking',
  printer_peripherals: '🖨️ Printer & Peripherals',
  virus_removal: '🦠 Virus Removal',
  smart_home: '🏠 Smart Home',
  tv_device_setup: '📺 TV & Device Setup',
  social_media: '📱 Social Media',
  pc_setup: '💻 PC Setup',
  social_media_account_recovery: '🔐 Account Recovery',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  pending: { label: 'Finding Tech', color: '#FF9F0A', dot: '#FF9F0A' },
  dispatched: { label: 'Dispatching', color: '#FF9F0A', dot: '#FF9F0A' },
  accepted: { label: 'Tech Accepted', color: '#30D158', dot: '#30D158' },
  en_route: { label: 'On the Way', color: '#30D158', dot: '#30D158' },
  arrived: { label: 'Arrived', color: '#30D158', dot: '#30D158' },
  in_progress: { label: 'In Progress', color: colors.primary, dot: colors.primary },
  completed: { label: 'Completed', color: '#30D158', dot: '#30D158' },
  cancelled: { label: 'Cancelled', color: colors.error, dot: colors.error },
  couldnt_fix: { label: "Couldn't Fix", color: colors.error, dot: colors.error },
};

const ACTIVE_STATUSES = ['pending', 'dispatched', 'accepted', 'en_route', 'arrived', 'in_progress', 'pending_tech_acceptance'];

export default function MyJobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [historyJobs, setHistoryJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'active' | 'history'>('active');

  useEffect(() => {
    if (!user) return;
    loadJobs();
  }, [user]);

  const loadJobs = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'jobs'),
        where('customerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const all: Job[] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Job));
      setActiveJobs(all.filter((j) => ACTIVE_STATUSES.includes(j.status)));
      setHistoryJobs(all.filter((j) => !ACTIVE_STATUSES.includes(j.status)));
    } catch (e) {
      console.error('Error loading jobs:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts?: Timestamp) => {
    if (!ts) return '';
    const d = ts.toDate();
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const displayJobs = tab === 'active' ? activeJobs : historyJobs;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 80,
      }}
    >
      <TopNav />

      <div style={{ padding: '16px 16px 0' }}>
        <h1 style={{ color: colors.textPrimary, fontSize: 24, fontWeight: 700, margin: '0 0 16px' }}>
          My Jobs
        </h1>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            background: colors.surface,
            borderRadius: 12,
            padding: 4,
            marginBottom: 20,
          }}
        >
          {(['active', 'history'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1,
                padding: '9px 0',
                background: tab === t ? colors.primary : 'transparent',
                border: 'none',
                borderRadius: 10,
                color: tab === t ? '#fff' : colors.textSecondary,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                transition: 'background 0.15s',
              }}
            >
              {t === 'active' ? `Active (${activeJobs.length})` : `History (${historyJobs.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: `3px solid ${colors.primary}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
                margin: '0 auto 10px',
              }}
            />
            <p style={{ color: colors.textSecondary }}>Loading jobs…</p>
          </div>
        ) : displayJobs.length === 0 ? (
          <div
            style={{
              background: colors.surface,
              borderRadius: 18,
              padding: '40px 24px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 52, marginBottom: 14 }}>
              {tab === 'active' ? '🔍' : '📋'}
            </div>
            <h3 style={{ color: colors.textPrimary, fontSize: 18, margin: '0 0 8px' }}>
              {tab === 'active' ? 'No active jobs' : 'No job history yet'}
            </h3>
            <p style={{ color: colors.textSecondary, fontSize: 14, margin: '0 0 20px' }}>
              {tab === 'active'
                ? 'Book a technician to get started'
                : 'Your completed jobs will appear here'}
            </p>
            {tab === 'active' && (
              <GradientButton onClick={() => navigate('/booking')} style={{ margin: '0 auto' }}>
                ⚡ Get Help Now
              </GradientButton>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {displayJobs.map((job) => {
              const statusInfo = STATUS_CONFIG[job.status] || { label: job.status, color: colors.textSecondary, dot: colors.textSecondary };
              const isActive = ACTIVE_STATUSES.includes(job.status);
              return (
                <div
                  key={job.id}
                  onClick={() => isActive ? navigate(`/job/${job.id}`) : job.status === 'completed' && navigate(`/review/${job.id}`)}
                  style={{
                    background: colors.surface,
                    borderRadius: 16,
                    padding: '16px',
                    cursor: isActive || job.status === 'completed' ? 'pointer' : 'default',
                    border: `1px solid ${isActive ? 'rgba(102,51,153,0.3)' : 'rgba(255,255,255,0.04)'}`,
                    transition: 'border-color 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                      <div style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15, marginBottom: 3 }}>
                        {CATEGORY_LABELS[job.category] || job.category}
                      </div>
                      {job.issueDescription && (
                        <div
                          style={{
                            color: colors.textSecondary,
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {job.issueDescription}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: statusInfo.dot,
                          animation: isActive ? 'pulse 1.5s infinite' : undefined,
                        }}
                      />
                      <span
                        style={{
                          color: statusInfo.color,
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                      {job.techName && (
                        <span style={{ color: colors.textSecondary, fontSize: 12 }}>
                          👤 {job.techName}
                        </span>
                      )}
                      {job.createdAt && (
                        <span style={{ color: colors.textSecondary, fontSize: 12 }}>
                          📅 {formatDate(job.createdAt)}
                        </span>
                      )}
                    </div>
                    {(isActive || job.status === 'completed') && (
                      <span style={{ color: colors.primary, fontSize: 13, fontWeight: 600 }}>
                        {isActive ? 'Track →' : job.status === 'completed' ? 'Rate →' : ''}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
